import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  LinearProgress,
  Grid,
  TextField,
  InputAdornment,
  MenuList,
  Snackbar,
  CircularProgress,
  Skeleton,
  Tooltip,
} from "@mui/material";
import {
  Backup,
  MoreVert,
  Download,
  Delete,
  Restore,
  CheckCircle,
  CloudUpload,
  Add,
  Search,
  FilterList,
  Refresh,
} from "@mui/icons-material";
import {
  fetchAllSitesBackups,
  createNewBackup,
  restoreSiteBackup,
  deleteSiteBackup,
  downloadSiteBackup,
} from "../redux/slices/backupSlice";
import { allSites } from "../redux/slices/siteSlice";
import CreateBackupDialog from "../components/dialog/CreateBackupDialog";
import { useSocket } from "../contexts/SocketContext";
import { mapBackupStatus, getStatusColor } from "../utils/backupStatus";

const Backups = () => {
  const dispatch = useDispatch();
  const { socket } = useSocket();
  const [anchorEl, setAnchorEl] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [messageSeverity, setMessageSeverity] = useState("success");
  const [backupInProgress, setBackupInProgress] = useState(null);
  const [selectedSite, setSelectedSite] = useState("");
  const [filters, setFilters] = useState({
    backupType: "",
    status: "",
    search: "",
  });
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const {
    allBackups,
    allBackupsLoading,
    allBackupsError,
    restoreLoading,
    deleteLoading,
    downloadLoading,
    allBackupsPagination,
  } = useSelector((state) => state.backup);

  const { site } = useSelector((state) => state.site);
  const { user } = useSelector((state) => state.user);
  const isVerifiedOrAdmin = user?.emailVerified || user?.role === "ADMIN";

  useEffect(() => {
    dispatch(allSites());
    dispatch(fetchAllSitesBackups());
  }, [dispatch]);

  useEffect(() => {
    const filterParams = {
      ...(selectedSite && { siteId: selectedSite }),
      ...(filters.backupType && { backupType: filters.backupType }),
      ...(filters.status && { status: filters.status }),
    };
    dispatch(fetchAllSitesBackups(filterParams));
  }, [selectedSite, filters, dispatch]);

  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleBackupStatus = (data) => {
      switch (data.status) {
        case "pending":
          setBackupInProgress("Backup queued... Waiting for worker");
          break;
        case "in_progress":
          setBackupInProgress(data.message || "Creating backup...");
          break;
        case "completed":
          setBackupInProgress(null);
          dispatch(
            fetchAllSitesBackups({
              ...(selectedSite && { siteId: selectedSite }),
              ...(filters.backupType && { backupType: filters.backupType }),
              ...(filters.status && { status: filters.status }),
            }),
          );
          setMessageSeverity("success");
          setSuccessMessage(
            `Backup completed successfully! Size: ${data.data?.sizeMB || data.sizeMB || "N/A"} MB`,
          );
          setTimeout(() => setSuccessMessage(""), 5000);
          break;
        case "failed":
          setBackupInProgress(null);
          dispatch(
            fetchAllSitesBackups({
              ...(selectedSite && { siteId: selectedSite }),
              ...(filters.backupType && { backupType: filters.backupType }),
              ...(filters.status && { status: filters.status }),
            }),
          );
          setMessageSeverity("error");
          setSuccessMessage(`Backup failed: ${data.error || "Unknown error"}`);
          setTimeout(() => setSuccessMessage(""), 5000);
          break;
        default:
          break;
      }
    };

    const handleRestoreStatus = (data) => {
      if (data.status === "completed" || data.status === "failed") {
        setSuccessMessage(
          data.status === "completed"
            ? "Site restored successfully!"
            : `Restore failed: ${data.error}`,
        );
        setTimeout(() => setSuccessMessage(""), 5000);
      }
    };

    socket.on("backup_status", handleBackupStatus);
    socket.on("restore_status", handleRestoreStatus);
    return () => {
      socket.off("backup_status", handleBackupStatus);
      socket.off("restore_status", handleRestoreStatus);
    };
  }, [socket, selectedSite, filters, dispatch]);

  const handleMenuOpen = (event, backup) => {
    setAnchorEl(event.currentTarget);
    setSelectedBackup(backup);
  };

  const handleDownloadMenuOpen = (event, backup) => {
    setDownloadMenuAnchor(event.currentTarget);
    setSelectedBackup(backup);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setDownloadMenuAnchor(null);
  };

  const handleOpenDialog = () => {
    if (!isVerifiedOrAdmin) {
      setMessageSeverity("error");
      setSuccessMessage(
        "Please verify your email address before performing this action.",
      );
      setTimeout(() => setSuccessMessage(""), 5000);
      return;
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleCreateBackup = async (siteId, payload) => {
    try {
      await dispatch(createNewBackup({ siteId, payload })).unwrap();
      setSuccessMessage("Backup started successfully!");
      handleCloseDialog();
    } catch (error) {
      throw new Error(error?.payload?.error || "Failed to create backup");
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;
    try {
      await dispatch(restoreSiteBackup(selectedBackup.id)).unwrap();
      setSuccessMessage("Restore process started successfully!");
      handleMenuClose();
    } catch (error) {
      throw new Error(error?.payload?.error || "Failed to restore backup");
    }
  };

  const handleDeleteBackup = async () => {
    if (!selectedBackup) return;
    try {
      await dispatch(deleteSiteBackup(selectedBackup.id)).unwrap();
      setSuccessMessage("Backup deleted successfully!");
      handleMenuClose();
    } catch (error) {
      throw new Error(error?.payload?.error || "Failed to delete backup");
    }
  };

  const handleDownloadBackup = async (fileType = "files") => {
    if (!selectedBackup) return;
    try {
      await dispatch(
        downloadSiteBackup({ backupId: selectedBackup.id, fileType }),
      ).unwrap();
      setSuccessMessage(`Downloading ${fileType} backup...`);
      handleMenuClose();
    } catch (error) {
      throw new Error(error?.payload?.error || "Failed to download backup");
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const clearFilters = () => {
    setSelectedSite("");
    setFilters({ backupType: "", status: "", search: "" });
  };

  const sites = site?.sites || [];
  const totalBackups = allBackupsPagination.totalCount || 0;
  const completedBackups = allBackups.filter(
    (b) => b.status === "COMPLETED",
  ).length;
  const totalSize = allBackups.reduce(
    (sum, backup) => sum + (parseFloat(backup.sizeMB) || 0),
    0,
  );

  const stats = [
    {
      label: "Total Backups",
      value: totalBackups,
      icon: <Backup sx={{ fontSize: 22 }} />,
      color: "#3b82f6",
    },
    {
      label: "Completed",
      value: completedBackups,
      icon: <CheckCircle sx={{ fontSize: 22 }} />,
      color: "#10b981",
    },
    {
      label: "Total Size",
      value: `${totalSize.toFixed(1)} MB`,
      icon: <CloudUpload sx={{ fontSize: 22 }} />,
      color: "#8b5cf6",
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Backups
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage and restore your site backups
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => dispatch(fetchAllSitesBackups())}
              disabled={allBackupsLoading}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenDialog}
            disableElevation
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            Create Backup
          </Button>
        </Box>
      </Box>

      {/* Status / Error Messages */}
      {successMessage && (
        <Alert
          severity={messageSeverity}
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}
      {allBackupsError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {allBackupsError}
        </Alert>
      )}

      {/* Stats Row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
          gap: 1.5,
          mb: 3,
        }}
      >
        {stats.map((s) => (
          <Box
            key={s.label}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.5,
                bgcolor: `${s.color}18`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: s.color,
                flexShrink: 0,
              }}
            >
              {s.icon}
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ lineHeight: 1.2 }}
              >
                {s.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {s.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          {/* Site Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Site</InputLabel>
            <Select
              value={selectedSite}
              label="Site"
              onChange={(e) => setSelectedSite(e.target.value)}
            >
              <MenuItem value="">All Sites</MenuItem>
              {sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.name} ({site.domain})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Type Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.backupType}
              label="Type"
              onChange={(e) => handleFilterChange("backupType", e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="MANUAL">Manual</MenuItem>
              <MenuItem value="SCHEDULED">Scheduled</MenuItem>
              <MenuItem value="PRE_UPDATE">Pre-Update</MenuItem>
              <MenuItem value="PRE_MIGRATION">Pre-Migration</MenuItem>
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="FAILED">Failed</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
            </Select>
          </FormControl>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search backups..."
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              shrink: isSearchFocused || !!filters.search,
              sx: {
                ...(!(isSearchFocused || !!filters.search) && {
                  marginLeft: "28px",
                }),
              },
            }}
          />

          {/* Clear Filters */}
          <Button
            fullWidth
            variant="outlined"
            onClick={clearFilters}
            startIcon={<FilterList />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              height: 40,
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Box>

      {/* Backups Table */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            All Backups {totalBackups > 0 && `(${totalBackups})`}
          </Typography>
        </Box>

        {allBackupsLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={56}
                sx={{ mx: 2.5, my: 1, borderRadius: 1 }}
              />
            ))}
          </Box>
        ) : allBackups.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: "2px dashed",
              borderColor: "divider",
              borderRadius: 2,
              m: 2.5,
            }}
          >
            <Backup sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              No backups found
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 360, mx: "auto" }}
            >
              {selectedSite || filters.backupType || filters.status
                ? "Try adjusting your filters"
                : "Create your first backup to get started"}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Backup />}
              onClick={handleOpenDialog}
              disableElevation
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Create First Backup
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Backup Name</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right" sx={{ width: 180, minWidth: 150 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allBackups.map((backup) => (
                  <TableRow key={backup.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {backup.backupName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {backup.site?.name || "Unknown Site"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {backup.site?.domain}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={backup.backupType}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem", height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {backup.sizeMB
                          ? `${backup.sizeMB} MB`
                          : "Calculating..."}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={mapBackupStatus(backup.status)}
                        size="small"
                        color={getStatusColor(backup.status)}
                        sx={{
                          textTransform: "capitalize",
                          fontSize: "0.7rem",
                          height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(backup.createdAt).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Download sx={{ fontSize: 14 }} />}
                          onClick={(e) => handleDownloadMenuOpen(e, backup)}
                          disabled={
                            backup.status !== "COMPLETED" || downloadLoading
                          }
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 1.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          Download
                        </Button>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, backup)}
                          disabled={
                            mapBackupStatus(backup.status) === "in_progress"
                          }
                          sx={{ color: "text.secondary" }}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Main Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={handleRestoreBackup}
          disabled={
            !selectedBackup ||
            selectedBackup.status !== "COMPLETED" ||
            restoreLoading
          }
        >
          <Restore sx={{ mr: 1, fontSize: 20 }} />
          Restore Backup
        </MenuItem>
        <MenuItem
          onClick={handleDeleteBackup}
          disabled={deleteLoading}
          sx={{ color: "error.main" }}
        >
          <Delete sx={{ mr: 1, fontSize: 20 }} />
          Delete Backup
        </MenuItem>
      </Menu>

      {/* Download Submenu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuList>
          <MenuItem onClick={() => handleDownloadBackup("files")}>
            <Download sx={{ mr: 1, fontSize: 20 }} />
            Download Files Backup
          </MenuItem>
          <MenuItem onClick={() => handleDownloadBackup("database")}>
            <Download sx={{ mr: 1, fontSize: 20 }} />
            Download Database Backup
          </MenuItem>
          <MenuItem onClick={() => handleDownloadBackup("metadata")}>
            <Download sx={{ mr: 1, fontSize: 20 }} />
            Download Metadata
          </MenuItem>
        </MenuList>
      </Menu>

      {/* Create Backup Dialog */}
      <CreateBackupDialog
        open={openDialog}
        onClose={handleCloseDialog}
        site={sites.find((s) => s.id === selectedSite)}
        onCreate={handleCreateBackup}
        sites={sites}
      />

      {/* Download Progress Snackbar */}
      <Snackbar
        open={downloadLoading}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{ width: "100%" }}
        >
          Downloading backup file... Please wait.
        </Alert>
      </Snackbar>

      {/* Backup In Progress Snackbar */}
      <Snackbar
        open={!!backupInProgress}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{ width: "100%" }}
        >
          {backupInProgress}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Backups;
